import React, { PropsWithChildren, useEffect, useRef, useState } from "react";

import * as Sentry from "@sentry/react";

const SENTRY_VENDOR_ID = 33;

interface ConsentData {
  nonTCFVendors?: Record<number, boolean>;
}

const isSentryEnabled = (consent: ConsentData): boolean => {
  const enabled = consent?.nonTCFVendors?.[SENTRY_VENDOR_ID];
  return enabled ?? false;
};

export const SentryProvider = ({ children }: PropsWithChildren) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const initializationAttempted = useRef(false);
  const isInitializedRef = useRef(isInitialized);

  useEffect(() => {
    isInitializedRef.current = isInitialized;
  }, [isInitialized]);

  const initializeSentry = () => {
    if (initializationAttempted.current) {
      return;
    }

    initializationAttempted.current = true;

    // Validate required environment variables
    const dsn = import.meta.env.VITE_SENTRY_DSN;
    if (!dsn) {
      console.warn("Sentry DSN not configured. Skipping initialization.");
      return;
    }

    try {
      Sentry.init({
        environment: import.meta.env.VITE_BAANA_ENVIRONMENT ?? "development",
        dsn,
        integrations: [
          Sentry.browserTracingIntegration(),
          Sentry.replayIntegration()
        ],
        tracesSampleRate: import.meta.env.PROD ? 0.5 : 1.0,
        tracePropagationTargets: [
          "localhost",
          ...(import.meta.env.VITE_BAANA_URL
            ? [import.meta.env.VITE_BAANA_URL]
            : [])
        ],
        replaysSessionSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
        replaysOnErrorSampleRate: 1.0
      });
      setIsInitialized(true);
    } catch (error) {
      initializationAttempted.current = false;
      console.error("Failed to initialize Sentry:", error);
    }
  };

  const handleConsentChange = async (event: CustomEvent) => {
    const consent = event.detail;

    if (consent) {
      const enabled = isSentryEnabled(consent);
      if (enabled && !isInitializedRef.current) {
        initializeSentry();
      } else if (!enabled && isInitializedRef.current) {
        initializationAttempted.current = false;
        await Sentry.close();
        setIsInitialized(false);
      }
    }
  };

  useEffect(() => {
    const events = [
      "alma_cmp_v3_consent_available_repetitive",
      "alma_cmp_v3_consent_changed"
    ];

    const eventListener = (event: Event) => {
      handleConsentChange(event as CustomEvent).catch((error) => {
        console.error("Error handling consent change:", error);
      });
    };

    for (const event of events) {
      document.addEventListener(event, eventListener);
    }

    return () => {
      for (const event of events) {
        document.removeEventListener(event, eventListener);
      }
    };
  }, []);

  return <>{children}</>;
};
